import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import './Inquisition.css';
import { useI18n } from '../i18n';
import { useAudio } from '../context/AudioContext';

const Inquisition = () => {
  const { t } = useI18n();
  const { speak, isSpeaking, stopAll } = useAudio();
  const [anomalies, setAnomalies] = useState([]);
  const [selectedAnomaly, setSelectedAnomaly] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    const initPage = async () => {
      await fetchHistory();
      try {
        await api.put('/inquisition/read-all');
        window.dispatchEvent(new Event('inquisition-read-all'));
        setAnomalies(prev => prev.map(a => ({ ...a, isRead: true })));
      } catch (err) {
        console.error('Failed to mark all as read:', err);
      }
    };
    initPage();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [selectedAnomaly]);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const res = await api.get('/inquisition/history?limit=50');
      setAnomalies(res.data.anomalies);

      // Auto-select first pending if none selected
      if (res.data.anomalies.length > 0) {
        const firstPending = res.data.anomalies.find(a => a.status === 'pending');
        setSelectedAnomaly(firstPending || res.data.anomalies[0]);
      }
    } catch (error) {
      console.error('Failed to fetch inquisition history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAnomaly = async (anomaly) => {
    setSelectedAnomaly(anomaly);
    setIsSidebarOpen(false); // Close sidebar on mobile after selecting

    if (!anomaly.isRead) {
      try {
        await api.put(`/inquisition/${anomaly.id}/read`);
        window.dispatchEvent(new Event('inquisition-read-all'));
        setAnomalies(prev => prev.map(a => a.id === anomaly.id ? { ...a, isRead: true } : a));
      } catch (e) {
        console.error('Failed to mark as read', e);
      }
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || !selectedAnomaly || selectedAnomaly.status === 'resolved') return;

    const message = inputValue;
    setInputValue('');
    setSending(true);

    // Optimistically update UI
    const updatedAnomaly = { ...selectedAnomaly };
    if (!updatedAnomaly.resolutionChat) {
      updatedAnomaly.resolutionChat = { messages: [] };
    }
    updatedAnomaly.resolutionChat.messages.push({ role: 'user', content: message });
    setSelectedAnomaly(updatedAnomaly);

    try {
      const res = await api.post(`/inquisition/chat/${selectedAnomaly.id}`, { message });

      // Update with AI response and new status
      const finalAnomaly = { ...updatedAnomaly };
      finalAnomaly.resolutionChat.messages.push({ role: 'ai', content: res.data.response });
      finalAnomaly.status = res.data.status;

      setSelectedAnomaly(finalAnomaly);

      // Update in list
      setAnomalies(prev => prev.map(a => a.id === finalAnomaly.id ? finalAnomaly : a));

    } catch (error) {
      console.error('Failed to send message:', error);
      // Revert optimistic update on error (simplified for brevity)
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const renderDesalineacion = (anomaly) => {
    let ctx = anomaly.contradictedContext;
    if (typeof ctx === 'string') {
      try {
        ctx = JSON.parse(ctx);
      } catch (e) {
        ctx = { raw: ctx };
      }
    }

    if (!ctx) ctx = {};

    const normalizeKey = (key) => {
      const lower = key.toLowerCase();
      // Try to map to explicit i18n keys first
      const exactMatchKeys = ['aiReason', 'profile', 'principles', 'input', 'userAction', 'lieReason', 'contradiction', 'context', 'module', 'severity', 'type'];
      const exactMatch = exactMatchKeys.find(k => k.toLowerCase() === lower);
      if (exactMatch) {
         const tVal = t(`inquisition.${exactMatch}`, '');
         if (tVal) return tVal;
      }

      // Fallback: Convert camelCase or snake_case to Title Case (best effort)
      const words = key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim().toLowerCase().split(' ');
      return words.map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    };

    const details = [];
    const mainKeys = ['input', 'userAction', 'lieReason', 'contradiction', 'aiReason', 'principles', 'profile', 'history', 'context', 'module', 'severity', 'type'];

    // Ordered extraction logic
    let inputVal = ctx.input || ctx.userAction || anomaly.userResponse;

    // 1. Clean Parsing for "Registered Statement" (Input)
    if (typeof inputVal === 'string' && inputVal.trim().startsWith('{')) {
      try {
        inputVal = JSON.parse(inputVal);
      } catch (e) {
        // ignore
      }
    }
    if (typeof inputVal === 'object' && inputVal !== null) {
      inputVal = inputVal.label || inputVal.value || JSON.stringify(inputVal).replace(/[{}[\]"]/g, '').replace(/:/g, ': ');
    }

    let contradictionVal = ctx.lieReason || ctx.contradiction || ctx.aiReason;
    let moduleVal = ctx.context || ctx.module || anomaly.sourceModule;
    let severityVal = ctx.severity || ctx.type;

    if (inputVal) {
      details.push({ title: t('inquisition.input', 'Afirmación Registrada'), value: inputVal, type: 'quote' });
    }
    if (contradictionVal) {
      details.push({ title: t('inquisition.lieReason', 'Disonancia Detectada'), value: contradictionVal, type: 'dissonance' });
    }
    if (moduleVal) {
      details.push({ title: t('inquisition.context', 'Módulo de Origen'), value: moduleVal, type: 'badge' });
    }
    if (severityVal) {
      details.push({ title: t('inquisition.severity', 'Nivel de Desalineación'), value: severityVal, type: 'badge' });
    }

    // Process specific narrative structures (principles and profile)
    if (ctx.principles) {
      let prin = ctx.principles;
      if (Array.isArray(prin)) {
        prin = prin[0];
      }
      if (typeof prin === 'string') {
        // 2. Strict TL;DR + Context
        const firstSentence = prin.split(/(?<=\.)\s/)[0] || prin;
        let displayPrin = firstSentence.endsWith('.') ? firstSentence : firstSentence + '.';

        const contextQuestion = ctx.question || ctx.prompt || ctx.action;
        if (contextQuestion) {
          displayPrin = `[Question: ${contextQuestion}] — ${displayPrin}`;
        }

        details.push({ title: t('inquisition.principles', 'Principio Transgredido'), value: displayPrin, type: 'text' });
      }
    }

    if (ctx.profile || ctx.history) {
      let prof = ctx.profile || ctx.history;
      if (typeof prof === 'string') {
        try { prof = JSON.parse(prof); } catch(e) {}
      }

      if (Array.isArray(prof)) {
         details.push({ title: t('inquisition.profile', 'Patrón Histórico Registrado'), value: prof, type: 'historyList' });
      } else if (typeof prof === 'string') {
         details.push({ title: t('inquisition.profile', 'Patrón Histórico Registrado'), value: prof, type: 'text' });
      }
    }

    // Add any other unknown keys
    Object.keys(ctx).forEach(key => {
      if (!mainKeys.includes(key) && ctx[key]) {
        let val = ctx[key];
        let valType = 'text';

        // Check if it's an array to render as list
        if (typeof val === 'string') {
           try {
              const parsed = JSON.parse(val);
              if (Array.isArray(parsed)) val = parsed;
           } catch(e) {}
        }

        if (Array.isArray(val)) {
           valType = 'historyList';
        } else if (typeof val === 'object') {
           // Still fallback to stringify for random unknown objects, but stripped of syntax
           val = JSON.stringify(val).replace(/[{}[\]"]/g, '').replace(/:/g, ': ');
        }
        details.push({ title: normalizeKey(key), value: val, type: valType });
      }
    });

    return (
      <div className="desalineacion-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', borderBottom: '1px solid #222', paddingBottom: '10px', marginBottom: '15px' }}>
          <h3 className="desalineacion-title" style={{ borderBottom: 'none', paddingBottom: 0, marginBottom: 0 }}>
            REPORTE DE DESALINEACIÓN
          </h3>
          <button
            className="inquisition-action-btn finalize-btn"
            style={{ whiteSpace: 'nowrap' }}
            onClick={() => {
              if (isSpeaking) {
                stopAll();
              } else {
                let compiledString = "Reporte de Desalineación... ";
                details.forEach(detail => {
                  if (detail.type !== 'historyList' && detail.type !== 'quote') {
                    let val = detail.value;
                    if (typeof val === 'string') val = val.replace(/^["']+|["']+$/g, '');
                    compiledString += detail.title + ": " + val + "... ";
                  }
                });
                speak(compiledString, { label: 'Reporte de Desalineación' });
              }
            }}
          >
            [ {isSpeaking ? '⏹ DETENER' : '🔊 ESCUCHAR REPORTE'} ]
          </button>
        </div>
        <div className="desalineacion-content">
          {details.map((detail, idx) => {
            let displayValue = detail.value;
            // 1. Force Truncation on the "Principles" Field
            const principlesTitle = t('inquisition.principles', 'Principio Transgredido');
            if ((detail.title === principlesTitle || detail.title === 'Principles' || detail.title === 'Principio Transgredido') && typeof detail.value === 'string') {
              const firstSentence = detail.value.split('. ')[0];
              displayValue = firstSentence.endsWith('.') ? firstSentence : firstSentence + '.';
            }

            if (detail.type === 'quote') {
              // 2. Clean Redundant Double Quotes "" from the User Input
              const cleanInput = (typeof displayValue === 'string' ? displayValue : String(displayValue)).replace(/^["']+|["']+$/g, '');
              return (
                <div key={idx} className="desalineacion-section">
                  <div className="desalineacion-label">{detail.title}</div>
                  <blockquote className="desalineacion-afirmacion">{cleanInput}</blockquote>
                </div>
              );
            }
            if (detail.type === 'dissonance') {
              return (
                <div key={idx} className="desalineacion-section">
                  <div className="desalineacion-label">{detail.title}</div>
                  <div className="desalineacion-disonancia">{detail.value}</div>
                </div>
              );
            }
            if (detail.type === 'badge') {
              return (
                <div key={idx} className="desalineacion-section badge-section">
                  <span className="desalineacion-label inline-label">{detail.title}:</span>
                  <span className="desalineacion-badge">{detail.value}</span>
                </div>
              );
            }
            if (detail.type === 'historyList') {
              const items = detail.value;
              return (
                <div key={idx} className="desalineacion-section">
                  <div className="desalineacion-label">{detail.title}</div>
                  <ul className="desalineacion-history-list">
                    {items.map((item, i) => {
                       let itemText = item;
                       if (typeof item === 'object') {
                          itemText = item.response || item.action || item.content || Object.values(item).filter(v => typeof v === 'string').join(' - ');
                          if (!itemText) itemText = JSON.stringify(item).replace(/[{}[\]"]/g, '').replace(/:/g, ': ');
                       }
                       return <li key={i}>{itemText}</li>;
                    })}
                  </ul>
                </div>
              );
            }

            return (
              <div key={idx} className="desalineacion-section">
                <span className="desalineacion-label inline-label">{detail.title}:</span>
                <span className="desalineacion-text"> {displayValue}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="inquisition-container"><div className="no-selection">{t('inquisition.accessingLogs')}</div></div>;
  }

  return (
    <div className="inquisition-container">
      {/* Mobile Toggle Button */}
      <button
        className="inquisition-mobile-toggle"
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
      >
        {t('inquisition.logs')}
      </button>

      <div className={`inquisition-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="inquisition-header">
          <h2>{t('inquisition.title')}</h2>
          {isSidebarOpen && (
            <button className="inquisition-close-sidebar" onClick={() => setIsSidebarOpen(false)}>×</button>
          )}
        </div>
        <div className="anomaly-list">
          {anomalies.map(anomaly => (
            <div
              key={anomaly.id}
              className={`anomaly-item ${selectedAnomaly?.id === anomaly.id ? 'active' : ''} ${anomaly.status === 'resolved' ? 'resolved' : ''} ${!anomaly.isRead ? 'unread' : ''}`}
              onClick={() => handleSelectAnomaly(anomaly)}
            >
              <div className={`anomaly-status status-${anomaly.status}`}>{anomaly.status}</div>
              <div className="anomaly-date">{new Date(anomaly.createdAt).toLocaleString()}</div>
              <div className="anomaly-module">{t('inquisition.module')} {anomaly.sourceModule}</div>
            </div>
          ))}
          {anomalies.length === 0 && <div style={{padding: '20px', color: '#555'}}>{t('inquisition.noAnomalies')}</div>}
        </div>
      </div>

      <div className="inquisition-main">
        {selectedAnomaly ? (
          <>
            <div className="inquisition-actions-bar">
              {selectedAnomaly.status !== 'resolved' && (
                <button
                  className="inquisition-action-btn finalize-btn"
                  onClick={async () => {
                    try {
                      await api.put(`/inquisition/${selectedAnomaly.id}/resolve`);
                      setAnomalies(prev => prev.map(a => a.id === selectedAnomaly.id ? { ...a, status: 'resolved' } : a));
                      setSelectedAnomaly({ ...selectedAnomaly, status: 'resolved' });
                    } catch(e) { console.error('Failed to resolve', e); }
                  }}
                >
                  FINALIZAR
                </button>
              )}
              <button
                className="inquisition-action-btn delete-btn"
                onClick={async () => {
                  try {
                    await api.delete(`/inquisition/${selectedAnomaly.id}`);
                    setAnomalies(prev => prev.filter(a => a.id !== selectedAnomaly.id));
                    setSelectedAnomaly(null);
                  } catch(e) { console.error('Failed to delete', e); }
                }}
              >
                BORRAR
              </button>
            </div>
            <div className="inquisition-chat-area">
              {renderDesalineacion(selectedAnomaly)}

              {selectedAnomaly.resolutionChat?.messages?.map((msg, idx) => {
                const formatMarkdown = (text) => {
                  if (!text) return null;

                  // Basic list parser
                  const lines = text.split('\n');
                  const blockElements = [];
                  let currentList = null;

                  lines.forEach((line, i) => {
                    const listMatch = line.match(/^(\s*)([-*])\s+(.+)$/);

                    const renderInline = (content, lineIdx) => {
                      const parts = content.split(/(\*\*.*?\*\*)/g);
                      return parts.map((part, j) => {
                        if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
                          return <strong key={`bold-${lineIdx}-${j}`}>{part.slice(2, -2)}</strong>;
                        }
                        return <span key={`text-${lineIdx}-${j}`}>{part}</span>;
                      });
                    };

                    if (listMatch) {
                      if (!currentList) {
                        currentList = [];
                      }
                      const content = listMatch[3];
                      currentList.push(
                        <li key={`li-${i}`} style={{ marginBottom: '5px' }}>
                          {renderInline(content, i)}
                        </li>
                      );
                    } else {
                      if (currentList) {
                        blockElements.push(
                          <ul key={`ul-${i}`} style={{ margin: '5px 0', paddingLeft: '20px' }}>
                            {currentList}
                          </ul>
                        );
                        currentList = null;
                      }

                      blockElements.push(
                        <div key={`line-${i}`} style={{ minHeight: '1em' }}>
                          {renderInline(line, i)}
                        </div>
                      );
                    }
                  });

                  if (currentList) {
                    blockElements.push(
                      <ul key={`ul-end`} style={{ margin: '5px 0', paddingLeft: '20px' }}>
                        {currentList}
                      </ul>
                    );
                  }

                  return <>{blockElements}</>;
                };

                return (
                  <div key={idx} className={`inquisition-message message-${msg.role}`}>
                    <div style={{ marginBottom: '8px', color: msg.role === 'ai' ? '#ff4500' : '#888' }}>
                      <strong>{msg.role === 'ai' ? t('inquisition.inquisitor') : t('inquisition.subject')}</strong>
                    </div>
                    <div>{formatMarkdown(msg.content)}</div>
                  </div>
                );
              })}
              <div ref={chatEndRef} />
            </div>

            <div className="inquisition-input-area">
              <textarea
                className="inquisition-input"
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={selectedAnomaly.status === 'resolved' ? t('inquisition.resolvedPlaceholder') : t('inquisition.explainPlaceholder')}
                disabled={sending || selectedAnomaly.status === 'resolved'}
                rows={3}
              />
              <button
                className="inquisition-submit"
                onClick={handleSendMessage}
                disabled={sending || !inputValue.trim() || selectedAnomaly.status === 'resolved'}
              >
                {sending ? '...' : t('inquisition.submit')}
              </button>
            </div>
          </>
        ) : (
          <div className="no-selection">{t('inquisition.selectAnomaly')}</div>
        )}
      </div>
    </div>
  );
};

export default Inquisition;
